/**
 * /wireline-slickline/wells/new — add a well (POST /wireline-slickline/wells,
 * fieldiq-api#1336). A full page, not a modal. On save the new well's own page
 * replaces this one, so Back lands on the register.
 *
 * WL_WELL_WRITE only (admin, office_staff, supervisor). Any other role is told
 * so here rather than handed a form the API would refuse. A 409 duplicate_well
 * shows the API's sentence, which names the existing well, and links to it.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWlWell, useWlCustomers, useWlMeta } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWriteWlWells, duplicateWlWell, wlRefusal } from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { WellForm } from './WellForm';
import { WL_EYEBROW, WlEmpty, WlLoading, WlPanel } from './WlBits';

export default function WlNewWellScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/wells');
  const { user } = useAuth();
  const canAdd = canWriteWlWells(user?.role);
  const meta = useWlMeta();
  const customers = useWlCustomers(canAdd);
  const create = useCreateWlWell();
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const customersFailure = queryLoadFailure(customers);

  return (
    <div data-testid="wl-new-well-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="New well" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel eyebrow="Well register" title="Well">
            {!canAdd ? (
              <WlEmpty>Only an admin, office staff or a supervisor can add a well.</WlEmpty>
            ) : meta.isLoading || customers.isLoading ? (
              <WlLoading label="Loading form" />
            ) : metaFailure || customersFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : customers)}
                kind={(metaFailure ?? customersFailure) || 'unreachable'}
                subject={metaFailure ? 'the well form' : 'your customers'}
              />
            ) : (customers.data ?? []).length === 0 ? (
              <WlEmpty>A well belongs to a customer, and there are no customers yet. Add one under Clients first.</WlEmpty>
            ) : (
              <WellForm
                well={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                customers={customers.data ?? []}
                pending={create.isPending}
                error={error}
                duplicateOf={duplicateOf}
                submitLabel="Create well"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  setDuplicateOf(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Well created');
                      if (res.data?.id) navigate(`/wireline-slickline/wells/${res.data.id}`, { replace: true });
                      else navigate('/wireline-slickline/wells', { replace: true });
                    },
                    onError: (err) => {
                      const dup = duplicateWlWell(err);
                      setError(dup ? dup.message : wlRefusal(err, 'The well could not be created.'));
                      setDuplicateOf(dup?.existingWellId ?? null);
                    },
                  });
                }}
              />
            )}
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
